// The admin dashboard's modal dialect — a skin over the shared @auctogon/ui
// Modal (BL-1247, ADR 030 § 3b Canon merge). Replaces `LazyModal`.
//
// WHAT CHANGED vs LazyModal: this component can now draw the chrome. Pass
// `title` (and optionally `subtitle` / `footer` / `size`) and it renders the
// white PanelHeader (BL-2305), the close X and the footer row for you. Admin previously
// left all of that to the caller, so ~66 call sites each hand-rolled a
// `bg-white rounded-lg shadow-xl max-w-*` card + their own PanelHeader — and
// drifted apart badly enough that BUG-1009 had to go re-unify the headers.
//
// WHAT DID NOT CHANGE: every LazyModal prop keeps its name and meaning, and a
// caller that passes no chrome gets byte-for-byte the old behaviour — backdrop
// + centring only, with its own panel div untouched. That is what let the
// migration be an import-path change for callers not yet lifted.
//
// PRESERVED FROM LazyModal (BUG-523): `{children}` is NOT rendered while
// `isOpen` is false — the component returns null early, so every hook, effect
// and fetch inside the modal's subtree stays dormant until the operator opens
// it. Do not "optimise" this into an always-mounted opacity toggle; that is
// exactly what the retired eager modal did and why BUG-523 exists.
//
// PRESERVED: the backdrop is a portal SIBLING of the container, not a child.
// Un-migrated callers supply a statically-positioned panel div, and CSS paints
// positioned descendants above non-positioned ones regardless of DOM order —
// a nested backdrop would cover them. Hence `backdrop={false}` +
// `closeOnContainerClick`, which is how LazyModal always dismissed.

'use client';

import { createContext, useContext, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import {
    Modal as UiModal,
    useBodyScrollLock,
    useMediaQuery,
    useVisualViewportBox,
    type ModalDialect,
} from '@auctogon/ui/web';
import { NO_SHEET_INTRO, SheetIntroContext, useSheetIntroHandle } from './sheet-intro-context';
import { ModalPresentationContext, SheetPresentation, useModalPresentation, type ModalPresentation } from './modal-presentation';

import { type PanelHeaderDensity } from './PanelHeader';
import { SheetChromePanel, chromeHeader } from './modal-chrome';
import { ADMIN_MODAL_SIZES, type AdminModalSize } from './modal-sizes';
import { OverlayLayerProvider, useOverlayZIndex } from './overlay-layer';

/**
 * Admin classic appearance. Denser than the buyer site's and squarer
 * (`rounded-lg` = 8px vs `rounded-panel` = 16px) — a deliberate dialect
 * difference, not drift (ADR 030 § 3b).
 */
const ADMIN_MODAL_DIALECT: ModalDialect = {
    // The viewport gutter does NOT live here — it is position-scoped, applied
    // alongside the centring classes below. A `position="right"` slide-out is
    // contractually flush to the viewport edge, and `clsx` concatenates rather
    // than resolving conflicting Tailwind utilities (ADR 030's BL-1004 hazard),
    // so a `p-4` stated here could not be cancelled by a later `p-0` — whichever
    // rule the JIT emitted last would win. Stating it once, in the branch that
    // wants it, is the only version with a single answer.
    container: 'fixed inset-0 flex',
    // Exactly the panel classes the seven lifted callers hand-rolled. Width
    // comes from the size map below.
    //
    // `max-h` + a scrolling body (not a scrolling panel — the panel is
    // `flex-col`, so the header and footer stay put while only the body
    // moves). Ceiling comes from `--modal-max-h`, the same token
    // `.ui-modal` reads in settings-v2.css: one number, one definition. Before
    // this, a tall form had no internal scroll at all, so on a phone with the
    // keyboard open the submit button sat below the fold with no way to reach
    // it.
    panel: 'bg-white rounded-lg shadow-xl w-full flex flex-col max-h-[var(--modal-max-h)]',
    // One definition, two consumers — `ConfirmDialog` reads the same map
    // (BUG-1620). See `./modal-sizes.ts` for why it is not restated here.
    size: ADMIN_MODAL_SIZES,
    // The body and footer gutters (BL-1258). Binding them HERE rather than
    // leaving them to callers is the whole point of the BL-1247 lift — ADR 030
    // merged Modal so that "ONE component owns panel + header + footer + size,
    // so chrome cannot drift per caller", and body padding was the one piece of
    // chrome that never moved in. It drifted exactly as predicted: `p-4` in
    // AddSellerModal, `p-6` in the design-gallery card, and NOTHING in the
    // clerk console's edit and withdraw modals, whose fields ran flush into the
    // panel edge.
    //
    // WHY tokens AND NOT `p-6`: admin already states this gutter once, in
    // `.ui-modal__body` (settings-v2.css) — the second, CSS-class modal chrome
    // that seven components still use. Writing `p-6` here would make that one
    // value true in two places that agree today and can silently disagree
    // tomorrow, which is the exact failure ADR 044 exists to stop. The sides
    // read `--overlay-gutter`, which the phone tier drops to 16px; the tokens
    // are on `:root`, so they resolve through the portal.
    body: 'py-[var(--space-8)] px-[var(--overlay-gutter)] overflow-y-auto overscroll-contain min-h-0',
    // No top padding: admin footers are `<FormFooter />`, which brings its own
    // `pt-4 mt-6 border-t`. This supplies only the horizontal gutter the bare
    // node never had (its buttons sat flush against the panel's right edge)
    // plus the bottom inset.
    footer: 'px-[var(--overlay-gutter)] pb-[var(--space-8)]',
    // No header/title/closeButton slots. Admin's header is always the white
    // `PanelHeader` node (BL-2305) (which draws its own close X — the one treatment
    // BUG-1009 consolidated), so the shared primitive's fallback title row and
    // close button are never reached. Binding them would be dead config.
};

/**
 * The enclosing Modal's automation namespace, read by `FormFooter` so a named
 * modal's Save / Cancel are `<testId>-save` / `<testId>-cancel` with no
 * per-caller edit (BL-1867). Every Modal provides its OWN value — `undefined`
 * when unnamed — so a nested id-less modal never inherits its parent's name.
 */
const ModalTestIdContext = createContext<string | undefined>(undefined);

/**
 * The automation namespace of the Modal this component renders inside.
 *
 * @returns The enclosing Modal's `testId`, or `undefined` outside one / in an unnamed one.
 */
export function useModalTestId(): string | undefined {
    return useContext(ModalTestIdContext);
}

/**
 * The phone tier a `phoneSheet` Modal goes full-screen on (BL-1998). The same
 * `max-width: 767px` the settings-v2 phone block and the BUG-1849 field floor
 * key on, so the sheet's geometry (here) and its chrome (`.ui-modal--sheet` in
 * settings-v2.css) switch at one width.
 */
export const PHONE_SHEET_QUERY = '(max-width: 767px)';

export { CardPresentation, SheetPresentation, useModalPresentation, type ModalPresentation } from './modal-presentation';

/**
 * The field size a form control should render at inside the enclosing Modal:
 * `'touch'` on the phone sheet (48px, 16px text — BUG-1860), `undefined`
 * (the control's own default) everywhere else. Pass it straight to the
 * control's `size` (`Select`, `DateTimePicker`, `TimeZoneSelect`) so a sheet
 * form has one field geometry without a per-caller media query.
 *
 * @returns `'touch'` inside a `phoneSheet` Modal on a phone, else `undefined`.
 */
export function useModalFieldSize(): 'touch' | undefined {
    return useModalPresentation() === 'sheet' ? 'touch' : undefined;
}

/**
 * Props for the admin Modal. The first block is `LazyModal`'s signature,
 * unchanged; the second is the chrome this component can now own.
 */
export interface ModalProps {
    /** Whether the modal is open */
    isOpen: boolean;
    /** Callback when the modal should close */
    onClose: () => void;
    /** Modal content — mounted only while `isOpen` is true */
    children: React.ReactNode;
    /** Accessible label for the dialog (announced by screen readers) */
    ariaLabel?: string;
    /** Optional custom class for the backdrop */
    backdropClassName?: string;
    /** Optional custom class for the modal container */
    containerClassName?: string;
    /** Whether clicking the backdrop closes the modal (default: true) */
    closeOnBackdropClick?: boolean;
    /** Whether pressing Escape closes the modal (default: true) */
    closeOnEscape?: boolean;
    /**
     * Z-index for the modal. Omit it and the modal layers itself relative to
     * any overlay it was opened from (see `./overlay-layer.tsx`) — 50 at the
     * page root, one step above an enclosing modal or slide-out. Pass a value
     * only to pin an exact layer.
     */
    zIndex?: number;
    /**
     * Anchor position. `'center'` (default) renders a centered card; `'right'`
     * renders a full-height slide-out panel flush against the right edge.
     */
    position?: 'center' | 'right';

    // ---- Chrome the component owns when you use it (BL-1247) ----

    /** Header title. Renders the white PanelHeader with the close X. */
    title?: string;
    /** Secondary text under the title. Requires `title`. */
    subtitle?: React.ReactNode;
    /**
     * PanelHeader density. Deliberately has NO default here — it is forwarded
     * as-is so `PanelHeader`'s own default (`panel`) applies. Defaulting it to
     * `form` in this skin would silently re-pad every lifted caller that had
     * been relying on that default.
     */
    headerDensity?: PanelHeaderDensity;
    /** Thumbnail element rendered at the left of the header. */
    headerThumbnail?: React.ReactNode;
    /** Metadata badges rendered under the subtitle. */
    headerMetadata?: React.ReactNode;
    /** Action buttons rendered in the header. */
    headerActions?: React.ReactNode;
    /** Locks the header's close X (PanelHeader `closeDisabled`), e.g. while a save is in flight. */
    headerCloseDisabled?: boolean;
    /** Footer content — typically a `<FormFooter />`. */
    footer?: React.ReactNode;
    /** Panel max-width key. Supplying it (or any other chrome prop) draws the panel. */
    size?: AdminModalSize;
    /** Extra classes on the panel. Only applies when the panel is drawn. */
    panelClassName?: string;
    /**
     * Automation handle on the `role="dialog"` root (BL-1875). Opt-in, like the
     * shared core's: several modals can be open at once, so there is no safe
     * default. A closed Modal renders nothing, so presence is the open state.
     * A `FormFooter` inside picks it up as `<testId>-save` / `<testId>-cancel`.
     */
    testId?: string;
    /**
     * On a phone (`PHONE_SHEET_QUERY`), present as a full-screen sheet instead
     * of the centred card (BL-1998): no gutter, pinned to the visual viewport so
     * a bottom action bar rides above the iOS keyboard, and no dismiss by
     * tapping outside — there is no outside. Escape still closes. A layout
     * variant (ADR 029 — View Units, Cluster 3); off by default, so every
     * other Modal is unchanged.
     *
     * Callers that bring their own `.ui-modal` panel add the `ui-modal--sheet`
     * class to it and read `useModalPresentation()` for the sheet-only bits.
     * On the chrome path (`title`), the sheet draws its own panel with the
     * `ModalHead` (BL-2405) and the children go in as they are: they render
     * their `.ui-modal__body` and `SheetActionBar` on the sheet.
     */
    phoneSheet?: boolean;
    /**
     * BL-2405: the opt-in to the sheet chrome, and the header title it shows.
     * With `phoneSheet` and a `title`, setting it draws the sheet panel with
     * `ModalHead` and the children instead of the card header, subtitle and
     * footer (Correct bid's sheet reads "Correct bid", its card "Correct bid
     * amount"). A titled `phoneSheet` Modal without it keeps today's panel and
     * footer on a phone (Send invoice, Mark as Open, Lot labels).
     */
    sheetTitle?: string;
}

/**
 * The centring layer's classes: a 16px viewport gutter on the centred card,
 * none for the right-hand slide-out or the phone sheet.
 *
 * @param isSheet - Whether the Modal is presented as the phone sheet.
 * @param position - The Modal's `position`.
 * @param extra - The caller's `containerClassName`.
 * @returns The container classes.
 */
function containerClasses(isSheet: boolean, position: 'center' | 'right', extra: string | undefined): string {
    return clsx(
        // `p-4` is a viewport gutter, not decoration: without it a
        // centred panel is `w-full` flush to both screen edges on a
        // phone. 16px is the phone overlay inset BL-1857 set for
        // `.ui-modal-backdrop`, and bidder-web's dialect — ADR 030
        // § 3b's canonical base for the Modal merge — carries the
        // same `p-4` (BL-1399; was `p-3`, 12px, until BL-1857).
        //
        // The `right` branch takes NO gutter: its contract (see
        // `position` above) is a full-height slide-out flush against
        // the right edge, and the caller's own `h-full w-full` child
        // resolves against this container's padding box — so a gutter
        // here insets the slide-out from all four edges at EVERY
        // width and leaves the backdrop visible around it.
        //
        // The phone sheet takes no gutter either: it IS the screen,
        // and it wins over `right` (BL-2108).
        //
        // The centred branch scrolls and centres SAFELY (BUG-1620, carried
        // over from the retired eager modal by BL-1710). This layer is the last
        // ancestor before the viewport and the body is scroll-locked, so with
        // plain `items-center` a bare caller panel taller than the window was
        // centred into negative space and clipped at both ends, with its title
        // and its submit unreachable. `safe` centres while the panel fits and
        // falls back to start-aligned when it does not; `overflow-y-auto`
        // makes the overflow reachable. A panel that fits is unmoved, and a
        // chrome panel never overflows (it is capped at `--modal-max-h`).
        isSheet
            ? 'items-stretch justify-stretch'
            : position === 'right'
              ? 'items-stretch justify-end'
              : // BUG-2294: from `md` the left gutter also clears the nav rail, so
                // the card centres over the main pane (in line with the bulk
                // bar). Padding, not an inset: a click over the rail still lands
                // on this layer and dismisses.
                'justify-center [align-items:safe_center] overflow-y-auto p-4 md:pl-rail-gutter',
        extra,
    );
}

/**
 * The contexts a phone sheet hands every control and header below it: the one
 * field geometry (BL-2108) and, so only a sheet hides its header title behind
 * an intro, the intro state (BL-2402). On the card both stay at their defaults.
 *
 * @param props.isSheet - Whether the Modal is presented as the phone sheet.
 * @param props.children - The Modal's subtree.
 * @returns The providers around `children`.
 */
function SheetContexts({ isSheet, children }: { isSheet: boolean; children: ReactNode }) {
    const intro = useSheetIntroHandle();
    return (
        <SheetIntroContext.Provider value={isSheet ? intro : NO_SHEET_INTRO}>
            {/* One tree at every width, so a rotation never remounts the form (BL-2234). */}
            <SheetPresentation active={isSheet}>{children}</SheetPresentation>
        </SheetIntroContext.Provider>
    );
}

/**
 * Modal dialog in the admin dashboard's dialect. Children's subtree mounts on
 * open and unmounts on close, so every hook inside is dormant while closed
 * (BUG-523). Handles the portal, body scroll lock, and overlay layering; the
 * shared primitive handles panel, header, footer, Escape and focus trapping.
 *
 * @param props - see {@link ModalProps}
 * @returns The portalled dialog, or nothing when closed.
 */
export default function Modal({
    isOpen,
    onClose,
    children,
    ariaLabel,
    backdropClassName,
    containerClassName,
    closeOnBackdropClick = true,
    closeOnEscape = true,
    zIndex: explicitZIndex,
    position = 'center',
    title,
    subtitle,
    headerDensity,
    headerThumbnail,
    headerMetadata,
    headerActions,
    headerCloseDisabled,
    footer,
    size,
    panelClassName,
    testId,
    phoneSheet = false,
    sheetTitle,
}: ModalProps) {
    const zIndex = useOverlayZIndex(explicitZIndex);
    // Names the dialog for assistive tech. The shared core derives that from ITS
    // OWN `title` prop, but admin hands it a pre-assembled `PanelHeader` node
    // instead — so without wiring this id through, `role="dialog"` would carry
    // neither `aria-label` nor `aria-labelledby` and screen readers would
    // announce an unnamed dialog. Pointing at the visible heading (rather than
    // duplicating the string into `aria-label`) keeps the two from drifting.
    const titleId = useId();

    // Lock body scroll while open — the shared iOS-safe, ref-counted lock
    // (BUG-1849). `overflow: hidden` alone let iOS pan the page behind.
    useBodyScrollLock(isOpen);

    // Both hooks run on every render (hooks before the early returns below);
    // the viewport subscription attaches only while a sheet is actually shown.
    const isPhone = useMediaQuery(PHONE_SHEET_QUERY);
    const presentation: ModalPresentation = phoneSheet && isPhone ? 'sheet' : 'card';
    const isSheet = presentation === 'sheet';
    // iOS never resizes the layout viewport for the keyboard, and `dvh` does
    // not track it, so a sheet pinned with `inset: 0` keeps its floor behind
    // the keyboard. The visual viewport box is the only keyboard-aware size
    // (BUG-1840); `bottom: auto` lets `top` + `height` win over `inset-0`.
    const viewportBox = useVisualViewportBox(isOpen && isSheet);

    // SSR safety. `LazyModal` used a `useState(false)` + `useEffect(setMounted)`
    // pair here, which forced a guaranteed double render on every mount and
    // tripped `react-hooks/set-state-in-effect`. A `typeof document` check gives
    // the identical guarantee with neither cost — the same trade bidder's
    // `ModalPortal` already made and documented.
    if (typeof document === 'undefined') return null;
    // The defining LazyModal property (BUG-523): bail before rendering — or
    // even evaluating — the children subtree when the modal is closed.
    if (!isOpen) return null;

    // Any chrome prop means the component draws the card. With none, the caller
    // supplies its own panel and we stay a backdrop-plus-centring shell. On the
    // phone sheet a titled Modal draws the sheet panel instead (BL-2405).
    const sheetChrome = isSheet && title != null && sheetTitle != null;
    const hasChrome = !sheetChrome && (title != null || footer != null || size != null);

    const header =
        title != null && !sheetChrome
            ? chromeHeader({ title, titleId, subtitle, headerDensity, headerThumbnail, headerMetadata, headerActions, headerCloseDisabled, onClose })
            : undefined;

    const modalContent = (
        <OverlayLayerProvider zIndex={zIndex}>
          <ModalTestIdContext.Provider value={testId}>
          <ModalPresentationContext.Provider value={presentation}>
          <SheetContexts isSheet={isSheet}>
            {/* Backdrop — a sibling of the container, matching LazyModal. See
                the file header for why it must not be nested. */}
            <div
                data-testid="modal-backdrop"
                className={clsx('fixed inset-0 bg-black/50', backdropClassName)}
                style={{ zIndex: zIndex - 1 }}
                aria-hidden="true"
            />
            <UiModal
                isOpen={isOpen}
                onClose={onClose}
                ariaLabel={ariaLabel ?? (sheetChrome ? sheetTitle : undefined)}
                // Point at PanelHeader's rendered heading when we drew one;
                // otherwise fall back to the caller's ariaLabel.
                ariaLabelledBy={header ? titleId : undefined}
                panel={hasChrome}
                header={header}
                footer={footer}
                size={size}
                // PanelHeader draws admin's close X when there's a title, and a
                // caller with no title draws its own. Either way the shared
                // primitive must not inject a second one — LazyModal never did.
                showCloseButton={false}
                testId={testId}
                // The skin renders the backdrop itself; dismissal comes from a
                // click landing on the centring layer, as LazyModal always did.
                backdrop={false}
                // A full-screen sheet has no "outside" to tap, and a stray tap
                // on its edge must not throw away a half-typed form.
                closeOnContainerClick={closeOnBackdropClick && !isSheet}
                closeOnEscape={closeOnEscape}
                // The sheet's first control is usually a text field; focusing
                // it on open raises the keyboard over the form (AC 7). Land on
                // the dialog itself; Tab still walks in, and focus still
                // returns to the opener on close.
                initialFocus={isSheet ? 'container' : 'first'}
                containerClassName={containerClasses(isSheet, position, containerClassName)}
                containerStyle={
                    viewportBox
                        ? { zIndex, top: viewportBox.top, height: viewportBox.height, bottom: 'auto' }
                        : { zIndex }
                }
                className={sheetChrome ? undefined : panelClassName}
                dialect={ADMIN_MODAL_DIALECT}
            >
                {sheetChrome ? (
                    <SheetChromePanel title={sheetTitle} onClose={onClose} closeDisabled={headerCloseDisabled} className={panelClassName}>
                        {children}
                    </SheetChromePanel>
                ) : (
                    children
                )}
            </UiModal>
          </SheetContexts>
          </ModalPresentationContext.Provider>
          </ModalTestIdContext.Provider>
        </OverlayLayerProvider>
    );

    return createPortal(modalContent, document.body);
}
